import React, { cloneElement, useState } from 'react'
// libs
import styled from 'styled-components'
// components
import MaterialPopover, { PopoverProps } from '@mui/material/Popover'
// themes
import { colors } from 'themes'
// utils
import { debounce } from 'lodash'

const PAPER_PROPS = {
  style: {
    color: colors.white,
    borderRadius: 3,
    padding: 10,
    background: colors.darkBlue500,
    opacity: 0.95
  }
}

const POPOVER_PROPS = {
  anchorOrigin: {
    vertical: 'top' as const,
    horizontal: 'left' as const
  },
  transformOrigin: {
    vertical: 'bottom' as const,
    horizontal: 'left' as const
  }
}

const POPOVER_ID = 'mouse-over-popover'

export const TriggerWrapper = styled.span`
  cursor: pointer;
`

const Popover = styled((props: PopoverProps) => (
  <MaterialPopover
    {...props}
    slotProps={{
      paper: {
        ...PAPER_PROPS,
        ...(props.slotProps.paper || {})
      }
    }}
  />
))`
  font-size: 13px;
  pointer-events: none;

  & > * {
    pointer-events: all;
  }
`

interface Props extends Omit<PopoverProps, 'open'> {
  trigger: React.ReactElement
  triggerMouseEnter?: (event: React.MouseEvent) => void
  // Optional guard evaluated on every mouse enter, against the trigger element itself: the popover
  // opens only when it returns true (e.g. a tooltip shown just for text that is actually truncated).
  shouldOpen?: (event: React.MouseEvent) => boolean
}

const HoverablePopover: React.FC<Props> = ({
  trigger,
  children,
  triggerMouseEnter,
  shouldOpen,
  PaperProps,
  ...rest
}) => {
  const [anchor, setAnchor] = useState(null)
  const [isOpened, setIsOpened] = useState(false)

  const handlePopoverOpen = (event: React.MouseEvent) => {
    setAnchor(event.currentTarget)
    setIsOpened(true)
  }

  const closePopover = () => {
    setIsOpened(false)
  }

  const handlePopoverClose = debounce(closePopover, 100)

  const handleMouseEnter = (event: React.MouseEvent) => {
    triggerMouseEnter?.(event)

    if (shouldOpen && !shouldOpen(event)) {
      return
    }

    handlePopoverOpen(event)
  }

  const paperProps = {
    paper: {
      onMouseEnter: () => handlePopoverClose.cancel(),
      onMouseLeave: closePopover,
      ...(PaperProps || {})
    }
  }

  return (
    <>
      <TriggerWrapper>
        {cloneElement(trigger, {
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handlePopoverClose,
          'aria-haspopup': 'true',
          'aria-owns': isOpened ? POPOVER_ID : undefined
        })}
      </TriggerWrapper>
      <Popover
        hideBackdrop
        id={POPOVER_ID}
        open={isOpened}
        anchorEl={anchor}
        disableRestoreFocus
        disableEnforceFocus
        slotProps={paperProps}
        onClose={handlePopoverClose}
        {...POPOVER_PROPS}
        {...rest}>
        {children}
      </Popover>
    </>
  )
}

export default HoverablePopover
